import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import type { EmojiId, EmojiProvider } from '@atlaskit/emoji';
import Heading from '@atlaskit/heading';
import { SmartCardProvider } from '@atlaskit/link-provider';
import ModalDialog, { ModalBody, ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	type AtlassianLinkPickerPluginConfig,
	Scope,
	useAtlassianPlugins,
} from '@atlassian/link-picker-atlassian-plugin';

import { createEmojiProviderWithUpload as createEmojiProvider } from '../../common/utils/EmojiProviderUtils';
import type { UpdateSpaceResponse } from '../../controllers/space-store/types';
import { useGetSpace } from '../../services/useGetSpace/useGetSpace';
import { useUpdateSpace } from '../../services/useUpdateSpace/useUpdateSpace';
import type { SpaceSource } from '../create-space-modal/types';
import { SmartLinkSourceRow } from '../space-landing-page/SmartLinkSourceRow';
import { SpaceModalForm, type SpaceModalFormValues } from '../space-modal-form/SpaceModalForm';

import messages from './messages';

const styles = cssMap({
	spinnerContainer: {
		paddingBlock: token('space.400'),
	},
	errorText: {
		paddingBlock: token('space.200'),
	},
});

function useSpaceSourcePlugins(cloudId: string) {
	const configs = useMemo<AtlassianLinkPickerPluginConfig[]>(
		() => [
			{
				tabConfig: { tabKey: 'confluence', tabTitle: 'Confluence' },
				products: ['confluence'],
				scope: Scope.ConfluenceContentType,
				cloudId,
			},
			{
				tabConfig: { tabKey: 'jira', tabTitle: 'Jira' },
				products: ['jira'],
				scope: Scope.JiraIssueDashboardBoardProjectFilter,
				cloudId,
			},
			{
				tabConfig: { tabKey: 'atlas', tabTitle: 'Atlas' },
				products: ['townsquare'],
				scope: Scope.TownsquareProject,
				cloudId,
			},
			{
				tabConfig: { tabKey: 'loom', tabTitle: 'Loom' },
				products: ['loom'],
				scope: Scope.Loom,
				cloudId,
				disableActivityClient: true,
			},
		],
		[cloudId],
	);
	return useAtlassianPlugins(configs);
}

export type ManageSpaceModalProps = {
	isOpen: boolean;
	onClose: () => void;
	spaceId: string;
	cloudId: string;
	/** Pre-populate the name field immediately (from the pill/store) while full data loads */
	initialName?: string;
	/** Pre-populate the emoji picker immediately (from the pill/store) while full data loads */
	initialEmoji?: string | null;
	emojiProvider?: Promise<EmojiProvider>;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	/** Called after the space is successfully updated via the BE */
	onSuccess?: (space: UpdateSpaceResponse, sources: SpaceSource[]) => void;
};

const MANAGE_SPACE_FORM_ID = 'manage-space-form';

export const ManageSpaceModal = ({
	isOpen,
	onClose,
	spaceId,
	cloudId,
	initialName,
	initialEmoji,
	emojiProvider,
	assistanceServiceParams,
	onSuccess,
}: ManageSpaceModalProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const defaultEmojiProvider = useMemo(() => createEmojiProvider(cloudId), [cloudId]);
	const resolvedEmojiProvider = emojiProvider ?? defaultEmojiProvider;
	const [isEmojiPickerOpen, setEmojiPickerOpen] = useState(false);

	const [selectedEmoji, setSelectedEmoji] = useState<EmojiId | undefined>(
		initialEmoji ? { id: initialEmoji, shortName: '', fallback: initialEmoji } : undefined,
	);

	const plugins = useSpaceSourcePlugins(cloudId);

	const product = assistanceServiceParams?.product ?? 'rovo';
	const experienceId = assistanceServiceParams?.experienceId ?? 'ai-mate';

	const {
		space,
		isLoading,
		error: loadError,
	} = useGetSpace({
		spaceId,
		cloudId,
		product,
		experienceId,
		enabled: isOpen,
	});

	useEffect(() => {
		if (space?.emoji) {
			setSelectedEmoji({
				id: space.emoji,
				shortName: '',
				fallback: space.emoji,
			});
		}
	}, [space?.emoji]);

	const handleEmojiSelection = useCallback((emojiId: EmojiId) => {
		setSelectedEmoji(emojiId);
		setEmojiPickerOpen(false);
	}, []);

	const reset = useCallback(() => {
		setEmojiPickerOpen(false);
		setSelectedEmoji(
			initialEmoji ? { id: initialEmoji, shortName: '', fallback: initialEmoji } : undefined,
		);
	}, [initialEmoji]);

	useEffect(() => {
		if (!isOpen) {
			reset();
		}
	}, [isOpen, reset]);

	const handleClose = useCallback(() => {
		reset();
		onClose();
	}, [onClose, reset]);

	const { mutate: updateSpace } = useUpdateSpace({
		spaceId,
		cloudId,
		product,
		experienceId,
	});

	const handleFormSubmit = useCallback(
		(values: SpaceModalFormValues) => {
			const trimmed = values.name.trim();
			if (!trimmed) {
				return;
			}

			const sources: SpaceSource[] = values.sources.map((s) => ({
				ari: s.ari,
				url: s.url,
				sourceType: s.sourceType,
				conversationIds: s.conversationIds,
				name: s.name,
			}));

			const optimisticSpace: UpdateSpaceResponse = {
				id: spaceId,
				name: trimmed,
				description: values.about.trim() || undefined,
				emoji: selectedEmoji?.id ?? null,
				owner: '',
				createdAt: '',
				updatedAt: '',
			};

			// Optimistically close and notify — fire-and-forget the BE call
			onSuccess?.(optimisticSpace, sources);
			handleClose();

			updateSpace({
				name: trimmed,
				description: values.about.trim() || undefined,
				emoji: selectedEmoji?.id ?? null,
				sources: sources.map((s) => ({
					ari: s.ari,
					url: s.url,
					sourceType: s.sourceType,
					name: s.name,
				})),
			});
		},
		[updateSpace, selectedEmoji, spaceId, onSuccess, handleClose],
	);

	const formMessages = useMemo(
		() => ({
			requiredFieldsHint: messages.manageSpaceRequiredFieldsHint,
			nameLabel: formatMessage(messages.manageSpaceNameLabel),
			nameRequired: formatMessage(messages.manageSpaceNameRequired),
			emojiPickerLabel: formatMessage(messages.manageSpaceEmojiPickerLabel),
			aboutLabel: formatMessage(messages.manageSpaceAboutLabel),
			sourcesLabel: formatMessage(messages.manageSpaceSourcesLabel),
			addSources: formatMessage(messages.manageSpaceAddSources),
			removeSource: (name: string | undefined) =>
				formatMessage(messages.manageSpaceRemoveSource, { name }),
			noSourcesTitle: formatMessage(messages.manageSpaceNoSourcesTitle),
			noSources: formatMessage(messages.manageSpaceNoSources),
		}),
		[formatMessage],
	);

	const renderSourceRow = useCallback(
		(source: SpaceSource) => (
			<SmartLinkSourceRow url={source.url} fallback={source.name ?? source.url} />
		),
		[],
	);

	if (!isOpen) {
		return null;
	}

	const initialSources: SpaceSource[] =
		space?.sources?.map((s) => ({
			ari: s.ari,
			url: s.url,
			sourceType: s.sourceType as SpaceSource['sourceType'],
			conversationIds: [],
			name: s.name,
		})) ?? [];

	const initialAbout = space?.description ?? '';
	const resolvedInitialName = space?.name ?? initialName ?? '';

	return (
		<SmartCardProvider>
			<ModalDialog onClose={handleClose} width="medium" testId="manage-space-modal">
				{/* eslint-disable-next-line @atlaskit/design-system/use-modal-title */}
				<ModalHeader hasCloseButton>
					<Heading size="medium">{formatMessage(messages.manageSpaceModalTitle)}</Heading>
				</ModalHeader>
				<ModalBody>
					{isLoading ? (
						<Flex justifyContent="center" xcss={styles.spinnerContainer}>
							<Spinner size="large" />
						</Flex>
					) : loadError ? (
						<Flex justifyContent="center" xcss={styles.errorText}>
							<Text color="color.text.danger">{formatMessage(messages.manageSpaceLoadError)}</Text>
						</Flex>
					) : (
						<SpaceModalForm
							formId={MANAGE_SPACE_FORM_ID}
							testId="manage-space"
							messages={formMessages}
							initialName={resolvedInitialName}
							initialAbout={initialAbout}
							initialSources={initialSources}
							selectedEmoji={selectedEmoji}
							onEmojiSelect={handleEmojiSelection}
							isEmojiPickerOpen={isEmojiPickerOpen}
							onEmojiPickerToggle={() => setEmojiPickerOpen((v) => !v)}
							onEmojiPickerClose={() => setEmojiPickerOpen(false)}
							emojiProvider={resolvedEmojiProvider}
							cloudId={cloudId}
							plugins={plugins}
							onSubmit={handleFormSubmit}
							renderSourceRow={renderSourceRow}
						/>
					)}
				</ModalBody>
				<ModalFooter>
					<Button appearance="subtle" onClick={handleClose}>
						{formatMessage(messages.manageSpaceCancel)}
					</Button>
					<Button
						appearance="primary"
						type="submit"
						form={MANAGE_SPACE_FORM_ID}
						testId="manage-space-submit"
						isDisabled={isLoading || !!loadError}
					>
						{formatMessage(messages.manageSpaceSubmit)}
					</Button>
				</ModalFooter>
			</ModalDialog>
		</SmartCardProvider>
	);
};
